import posthog from 'posthog-js'

import AnalyticsService, { IAnalyticsProvider } from './AnalyticsService'
import authService from '../AuthService'
import { AnalyticsEvent } from './AnalyticsEvent'
import packageJson from '../../../package.json'
import versionJson from '../../version.json'

/**
 * PostHogProvider
 *
 * Implements PostHog analytics tracking following the existing provider pattern.
 * PostHog provides product analytics, session recording, feature flags, and more.
 *
 * @usage
 * const php = new PostHogProvider()
 * php.tokenId = config.posthogApiKey
 * php.apiHost = config.posthogApiHost
 * analyticsService.registerProvider('PostHog', php)
 *
 * @documentation
 * https://posthog.com/docs/libraries/js
 * https://posthog.com/docs/integrate/client/js
 */
export class PostHogProvider
  extends AnalyticsService
  implements IAnalyticsProvider
{
  apiHost: string = 'https://app.posthog.com'

  initProvider = () => {
    if (!this.tokenId) {
      console.warn('PostHogProvider - No API key provided')
      return
    }

    this.logEvent(`PostHogProvider initProvider`)

    posthog.init(this.tokenId, {
      api_host: this.apiHost,
      loaded: () => {
        this.logEvent('PostHog loaded successfully')
        // Register user after PostHog loads
        this.registerUser()
      },
      // Disable automatic pageview capture as we handle it manually
      capture_pageview: false,
      // Enable session recording (can be toggled later)
      disable_session_recording: false,
      // Persist user data across sessions
      persistence: 'localStorage+cookie',
      // Privacy settings
      session_recording: {
        maskAllInputs: true,
        maskInputOptions: {
          password: true,
          email: true
        }
      },
      // Debug mode based on analytics service setting
      debug: this.enableDebugLogging
    })
  }

  registerUser = async () => {
    try {
      const user = await authService.getUserAsync()

      if (!user?.profile.sub) {
        this.logEvent('PostHogProvider - No user to identify')
        return
      }

      const userId = user.profile.sub

      // Identify the user in PostHog
      posthog.identify(userId, {
        email: user.profile.email,
        version: packageJson.version,
        build:
          versionJson.buildNumber !== '#{BuildNumber}#'
            ? versionJson.buildNumber
            : '0',
        firstLoginDate: new Date().toISOString()
      })

      this.logEvent(`PostHogProvider - User identified: ${userId}`)
    } catch (error) {
      console.error('PostHogProvider - Error identifying user:', error)
    }
  }

  trackEvent = (event: AnalyticsEvent, properties?: object) => {
    this.logEvent(
      `PostHogProvider trackEvent: ${event}, ${JSON.stringify(properties)}`
    )

    if (posthog && typeof posthog.capture === 'function') {
      posthog.capture(event.toString(), properties)
    }
  }

  trackPageView = (path: string, properties?: object) => {
    this.logEvent(`PostHogProvider trackPageView: ${path}`)

    if (posthog && typeof posthog.capture === 'function') {
      posthog.capture('$pageview', {
        $current_url: path,
        route: path,
        ...properties
      })
    }
  }

  trackError = (error: Error, properties?: object) => {
    this.logEvent(`PostHogProvider trackError: ${error.message}`)

    if (posthog && typeof posthog.captureException === 'function') {
      posthog.captureException(error, properties)
    }
  }

  /**
   * Reset user identification on logout
   */
  reset = () => {
    if (posthog && typeof posthog.reset === 'function') {
      posthog.reset()
      this.logEvent('PostHogProvider - User reset')
    }
  }

  /**
   * Update user properties after initial identification
   */
  updateUserProperties = (properties: object) => {
    if (posthog && typeof posthog.setPersonProperties === 'function') {
      posthog.setPersonProperties(properties)
      this.logEvent(
        `PostHogProvider - User properties updated: ${JSON.stringify(
          properties
        )}`
      )
    }
  }
}
